import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../CSS/JobUpload.css';

const EditPlacementNews: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    placementTitle: '',
    placementSource: '',
    placementSourceIcon: null as File | null,
    placementPicture: null as File | null,
    placementShortDesc: '',
    placementLongDesc: '',
    createdDate: ''
  });

  const [shortDescCharCount, setShortDescCharCount] = useState(0);
  const [longDescCharCount, setLongDescCharCount] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [placementId, setPlacementId] = useState<number | null>(null);
  const [existingSourceIconUrl, setExistingSourceIconUrl] = useState<string>('');
  const [existingPictureUrl, setExistingPictureUrl] = useState<string>('');
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showHighlightColorPicker, setShowHighlightColorPicker] = useState(false);
  const [showTextColorPickerShort, setShowTextColorPickerShort] = useState(false);
  const [showHighlightColorPickerShort, setShowHighlightColorPickerShort] = useState(false);
  const shortDescEditorRef = useRef<HTMLDivElement>(null);
  const longDescEditorRef = useRef<HTMLDivElement>(null);
  const fontFamilyRef = useRef<HTMLSelectElement>(null);
  const fontSizeRef = useRef<HTMLSelectElement>(null);
  const fontFamilyRefShort = useRef<HTMLSelectElement>(null);
  const fontSizeRefShort = useRef<HTMLSelectElement>(null);
  const textColorPickerRef = useRef<HTMLDivElement>(null);
  const highlightColorPickerRef = useRef<HTMLDivElement>(null);
  const textColorPickerRefShort = useRef<HTMLDivElement>(null);
  const highlightColorPickerRefShort = useRef<HTMLDivElement>(null);

  const MAX_SHORT_DESC = 5000;
  const MAX_LONG_DESC = 50000;

  // Load data from query parameter on mount
  useEffect(() => {
    const idParam = searchParams.get('placementId');
    if (idParam) {
      const id = parseInt(idParam);
      if (!isNaN(id)) {
        fetchPlacementData(id);
      }
    }
  }, [searchParams]);

  // Load editor content when formData is populated
  useEffect(() => {
    if (shortDescEditorRef.current && formData.placementShortDesc && !shortDescEditorRef.current.innerHTML) {
      shortDescEditorRef.current.innerHTML = formData.placementShortDesc;
      updateShortDescContent();
    }
  }, [formData.placementShortDesc]);

  useEffect(() => {
    if (longDescEditorRef.current && formData.placementLongDesc && !longDescEditorRef.current.innerHTML) {
      longDescEditorRef.current.innerHTML = formData.placementLongDesc;
    updateLongDescContent();
    }
  }, [formData.placementLongDesc]);

  const fetchPlacementData = async (id: number) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      const response = await fetch(`http://127.0.0.1:3060/api/placements/${id}`, {
        method: 'GET',
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to fetch placement');
      }

      if (data.success && data.data) {
        const placement = data.data;
        setPlacementId(placement.placementId);
        
        // Format createdDate for date input (YYYY-MM-DD)
        const formatDateForInput = (dateString: string): string => {
          if (!dateString) return '';
          const date = new Date(dateString);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        setFormData({
          placementTitle: placement.placementTitle || '',
          placementSource: placement.placementSource || '',
          placementSourceIcon: null,
          placementPicture: null,
          placementShortDesc: placement.placementShortDesc || '',
          placementLongDesc: placement.placementLongDesc || '',
          createdDate: formatDateForInput(placement.createdDate || '')
        });

        setShortDescCharCount(placement.placementShortDesc?.length || 0);
        setLongDescCharCount(placement.placementLongDesc?.length || 0);
        setExistingSourceIconUrl(placement.placementSourceIcon || '');
        setExistingPictureUrl(placement.placementPicture || '');

        // Load editor content after a short delay to ensure refs are ready
        setTimeout(() => {
          if (shortDescEditorRef.current && placement.placementShortDesc) {
            shortDescEditorRef.current.innerHTML = placement.placementShortDesc;
            updateShortDescContent();
          }
          if (longDescEditorRef.current && placement.placementLongDesc) {
            longDescEditorRef.current.innerHTML = placement.placementLongDesc;
            updateLongDescContent();
          }
        }, 100);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Error fetching placement:', error);
      alert(error.message || 'Error fetching placement. Please try again.');
      navigate('/admin/placement-directory');
    } finally {
      setIsLoading(false);
    }
  };

  // Formatting functions for WYSIWYG editor
  const formatText = (command: string, value?: string, editor: 'short' | 'long' = 'long') => {
    const editorRef = editor === 'short' ? shortDescEditorRef : longDescEditorRef;
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command, false, value);
      if (editor === 'short') {
        updateShortDescContent();
      } else {
        updateLongDescContent();
      }
    }
  };

  const updateShortDescContent = () => {
    if (shortDescEditorRef.current) {
      const htmlContent = shortDescEditorRef.current.innerHTML;
      const textContent = shortDescEditorRef.current.textContent || '';
      setShortDescCharCount(textContent.length);
      setFormData(prev => ({ ...prev, placementShortDesc: htmlContent }));
    }
  };

  const updateLongDescContent = () => {
    if (longDescEditorRef.current) {
      const htmlContent = longDescEditorRef.current.innerHTML;
      const textContent = longDescEditorRef.current.textContent || '';
      setLongDescCharCount(textContent.length);
      setFormData(prev => ({ ...prev, placementLongDesc: htmlContent }));
    }
  };

  const handleShortDescInput = () => {
    updateShortDescContent();
  };

  const handleLongDescInput = () => {
    updateLongDescContent();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: 'placementSourceIcon' | 'placementPicture') => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({
      ...prev,
      [fieldName]: file
    }));
  };

  const handlePaste = (e: React.ClipboardEvent, editor: 'short' | 'long' = 'long') => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    if (editor === 'short') {
      updateShortDescContent();
    } else {
      updateLongDescContent();
    }
  };

  const handleFontFamilyChange = (editor: 'short' | 'long' = 'long') => {
    const fontRef = editor === 'short' ? fontFamilyRefShort : fontFamilyRef;
    if (fontRef.current) {
      formatText('fontName', fontRef.current.value, editor);
    }
  };

  const handleFontSizeChange = (editor: 'short' | 'long' = 'long') => {
    const sizeRef = editor === 'short' ? fontSizeRefShort : fontSizeRef;
    if (sizeRef.current) {
      formatText('fontSize', sizeRef.current.value, editor);
    }
  };

  const handleTextColorClick = (e: React.MouseEvent, editor: 'short' | 'long' = 'long') => {
    e.stopPropagation();
    if (editor === 'short') {
      setShowTextColorPickerShort(!showTextColorPickerShort);
      setShowHighlightColorPickerShort(false);
    } else {
      setShowTextColorPicker(!showTextColorPicker);
      setShowHighlightColorPicker(false);
    }
  };

  const handleHighlightColorClick = (e: React.MouseEvent, editor: 'short' | 'long' = 'long') => {
    e.stopPropagation();
    if (editor === 'short') {
      setShowHighlightColorPickerShort(!showHighlightColorPickerShort);
      setShowTextColorPickerShort(false);
    } else {
      setShowHighlightColorPicker(!showHighlightColorPicker);
      setShowTextColorPicker(false);
    }
  };

  const handleTextColorSelect = (color: string, editor: 'short' | 'long' = 'long') => {
    formatText('foreColor', color, editor);
    if (editor === 'short') {
      setShowTextColorPickerShort(false);
    } else {
      setShowTextColorPicker(false);
    }
  };

  const handleHighlightColorSelect = (color: string, editor: 'short' | 'long' = 'long') => {
    formatText('backColor', color, editor);
    if (editor === 'short') {
      setShowHighlightColorPickerShort(false);
    } else {
      setShowHighlightColorPicker(false);
    }
  };

  // Standard color palette (similar to Google Docs)
  const colorPalette = [
    '#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF',
    '#980000', '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#4A86E8', '#0000FF', '#9900FF', '#FF00FF',
    '#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC', '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC',
    '#DD7E6B', '#EA9999', '#F9CB9C', '#FFE599', '#B6D7A8', '#A2C4C9', '#A4C2F4', '#9FC5E8', '#B4A7D6', '#D5A6BD',
    '#CC4125', '#E06666', '#F6B26B', '#FFD966', '#93C47D', '#76A5AF', '#6D9EEB', '#6FA8DC', '#8E7CC3', '#C27BA0',
    '#A61C00', '#CC0000', '#E69138', '#F1C232', '#6AA84F', '#45818E', '#3C78D8', '#3D85C6', '#674EA7', '#A64D79',
    '#85200C', '#990000', '#B45F06', '#BF9000', '#38761D', '#134F5C', '#1155CC', '#0B5394', '#351C75', '#741B47',
    '#5B0F00', '#660000', '#783F04', '#7F6000', '#274E13', '#0C343D', '#1C4587', '#073763', '#20124D', '#4C1130'
  ];

  // Close color pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedInsideTextColor = textColorPickerRef.current?.contains(target);
      const clickedInsideHighlightColor = highlightColorPickerRef.current?.contains(target);
      const clickedInsideTextColorShort = textColorPickerRefShort.current?.contains(target);
      const clickedInsideHighlightColorShort = highlightColorPickerRefShort.current?.contains(target);
      
      if (!clickedInsideTextColor && !clickedInsideHighlightColor && 
          !clickedInsideTextColorShort && !clickedInsideHighlightColorShort) {
        setShowTextColorPicker(false);
        setShowHighlightColorPicker(false);
        setShowTextColorPickerShort(false);
        setShowHighlightColorPickerShort(false);
      }
    };

    if (showTextColorPicker || showHighlightColorPicker || showTextColorPickerShort || showHighlightColorPickerShort) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTextColorPicker, showHighlightColorPicker, showTextColorPickerShort, showHighlightColorPickerShort]);

  const validateForm = (): boolean => {
    if (!formData.placementTitle.trim()) {
      alert('Placement Article Title is required');
      return false;
    }
    if (!formData.placementSource.trim()) {
      alert('Placement Article Source is required');
      return false;
    }
    if (!formData.placementShortDesc.trim()) {
      alert('Placement Article Short Description is required');
      return false;
    }
    if (shortDescCharCount > MAX_SHORT_DESC) {
      alert(`Short Description exceeds the maximum character limit of ${MAX_SHORT_DESC}`);
      return false;
    }
    if (!formData.placementLongDesc.trim()) {
      alert('Placement Article Long Description is required');
      return false;
    }
    if (longDescCharCount > MAX_LONG_DESC) {
      alert(`Long Description exceeds the maximum character limit of ${MAX_LONG_DESC}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowConfirmation(false);
      return;
    }

    if (!placementId) {
      alert('No placement ID available');
      return;
    }

    setIsSubmitting(true);
    try {
      // Create FormData for file uploads
      const submitData = new FormData();
      submitData.append('placementTitle', formData.placementTitle.trim());
      submitData.append('placementSource', formData.placementSource.trim());
      
      if (formData.placementSourceIcon) {
        submitData.append('placementSourceIcon', formData.placementSourceIcon);
      }
      
      if (formData.placementPicture) {
        submitData.append('placementPicture', formData.placementPicture);
      }
      
      submitData.append('placementShortDesc', formData.placementShortDesc.trim());
      submitData.append('placementLongDesc', formData.placementLongDesc.trim());
      
      // Add createdDate if provided
      if (formData.createdDate.trim()) {
        submitData.append('createdDate', formData.createdDate.trim());
      }

      // Get token from localStorage or sessionStorage
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      const response = await fetch(`http://127.0.0.1:3060/api/placements/${placementId}`, {
        method: 'PUT',
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {},
        body: submitData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to update placement');
      }

      alert('Placement news updated successfully!');
      setShowConfirmation(false);
      navigate('/admin/placement-directory');
    } catch (error: any) {
      console.error('Error updating placement:', error);
      alert(error.message || 'Error updating placement. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setShowConfirmation(true);
    }
  };

  // Helper function to render WYSIWYG toolbar
  const renderWysiwygToolbar = (editor: 'short' | 'long') => {
    const currentTextColorPickerRef = editor === 'short' ? textColorPickerRefShort : textColorPickerRef;
    const currentHighlightColorPickerRef = editor === 'short' ? highlightColorPickerRefShort : highlightColorPickerRef;
    const showTextColor = editor === 'short' ? showTextColorPickerShort : showTextColorPicker;
    const showHighlight = editor === 'short' ? showHighlightColorPickerShort : showHighlightColorPicker;

    return (
      <div className="wysiwyg-toolbar">
        <button type="button" onClick={() => formatText('bold', undefined, editor)} className="toolbar-btn" title="Bold">
          <strong>B</strong>
        </button>
        <button type="button" onClick={() => formatText('italic', undefined, editor)} className="toolbar-btn" title="Italic">
          <em>I</em>
        </button>
        <button type="button" onClick={() => formatText('underline', undefined, editor)} className="toolbar-btn" title="Underline">
          <u>U</u>
        </button>
        <div className="toolbar-color-picker-wrapper" ref={currentTextColorPickerRef}>
          <button type="button" onClick={(e) => handleTextColorClick(e, editor)} className="toolbar-btn toolbar-btn-color" title="Text Color">
            <span className="color-icon">A</span>
          </button>
          {showTextColor && (
            <div className="color-picker-popup">
              <div className="color-palette-grid">
                {colorPalette.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className="color-palette-item"
                    style={{ backgroundColor: color }}
                    onClick={() => handleTextColorSelect(color, editor)}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="toolbar-color-picker-wrapper" ref={currentHighlightColorPickerRef}>
          <button type="button" onClick={(e) => handleHighlightColorClick(e, editor)} className="toolbar-btn toolbar-btn-highlight" title="Highlight Color">
            <span className="highlight-icon">✎</span>
          </button>
          {showHighlight && (
            <div className="color-picker-popup">
              <div className="color-palette-grid">
                {colorPalette.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className="color-palette-item"
                    style={{ backgroundColor: color }}
                    onClick={() => handleHighlightColorSelect(color, editor)}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <button type="button" onClick={() => formatText('justifyLeft', undefined, editor)} className="toolbar-btn toolbar-btn-align-left" title="Align Left">
          <span className="align-icon-left"></span>
        </button>
        <button type="button" onClick={() => formatText('justifyCenter', undefined, editor)} className="toolbar-btn toolbar-btn-align-center" title="Align Center">
          <span className="align-icon-center"></span>
        </button>
        <button type="button" onClick={() => formatText('justifyRight', undefined, editor)} className="toolbar-btn toolbar-btn-align-right" title="Align Right">
          <span className="align-icon-right"></span>
        </button>
        <button type="button" onClick={() => formatText('justifyFull', undefined, editor)} className="toolbar-btn toolbar-btn-align-justify" title="Justify">
          <span className="align-icon-justify"></span>
        </button>
        <button type="button" onClick={() => formatText('insertOrderedList', undefined, editor)} className="toolbar-btn" title="Numbered List">
          1.
        </button>
        <button type="button" onClick={() => formatText('insertUnorderedList', undefined, editor)} className="toolbar-btn" title="Bullet List">
          •
        </button>
        <button type="button" onClick={() => formatText('outdent', undefined, editor)} className="toolbar-btn" title="Decrease Indent">
          ←
        </button>
        <button type="button" onClick={() => formatText('indent', undefined, editor)} className="toolbar-btn" title="Increase Indent">
          →
        </button>
        <button type="button" onClick={() => formatText('formatBlock', 'blockquote', editor)} className="toolbar-btn" title="Blockquote">
          "
        </button>
        <button type="button" onClick={() => formatText('formatBlock', 'pre', editor)} className="toolbar-btn" title="Code Block">
          &lt;/&gt;
        </button>
        <button type="button" onClick={() => formatText('insertHorizontalRule', undefined, editor)} className="toolbar-btn" title="Horizontal Rule">
          ─
        </button>
        <button type="button" onClick={() => formatText('undo', undefined, editor)} className="toolbar-btn" title="Undo">
          ↶
        </button>
        <button type="button" onClick={() => formatText('redo', undefined, editor)} className="toolbar-btn" title="Redo">
          ↷
        </button>
        <select
          ref={editor === 'short' ? fontFamilyRefShort : fontFamilyRef}
          onChange={() => handleFontFamilyChange(editor)}
          className="toolbar-select"
          title="Font Family"
        >
          <option value="">Font</option>
          <option value="Arial">Arial</option>
          <option value="Helvetica">Helvetica</option>
          <option value="Times New Roman">Times New Roman</option>
          <option value="Courier New">Courier New</option>
          <option value="Verdana">Verdana</option>
          <option value="Georgia">Georgia</option>
        </select>
        <select
          ref={editor === 'short' ? fontSizeRefShort : fontSizeRef}
          onChange={() => handleFontSizeChange(editor)}
          className="toolbar-select"
          title="Font Size"
        >
          <option value="">Size</option>
          <option value="1">8pt</option>
          <option value="2">10pt</option>
          <option value="3">12pt</option>
          <option value="4">14pt</option>
          <option value="5">18pt</option>
          <option value="6">24pt</option>
          <option value="7">36pt</option>
        </select>
        <button type="button" onClick={() => formatText('removeFormat', undefined, editor)} className="toolbar-btn" title="Clear Formatting">
          T↗
        </button>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="job-upload-page">
        <div className="job-upload-container" style={{ width: '90vw' }}>
          <h1 className="job-upload-title">Loading...</h1>
        </div>
            </div>
    );
  }

  if (!placementId) {
    return (
      <div className="job-upload-page">
        <div className="job-upload-container" style={{ width: '90vw' }}>
          <h1 className="job-upload-title">Edit Placement News</h1>
          <p>No placement ID provided. Please navigate from the Placement Directory.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="job-upload-page">
      <div className="job-upload-container" style={{ width: '90vw' }}>
        <h1 className="job-upload-title">Please provide the following information</h1>
        
        <form className="job-upload-form" onSubmit={handleFormSubmit}>
          <div className="job-upload-form-columns">
            {/* Left Column */}
            <div className="job-upload-column job-upload-column-left">
          <div className="job-upload-field">
            <label htmlFor="placementTitle" className="job-upload-label">
              Placement Article Title <span className="required">*</span>
            </label>
            <input
              type="text"
              id="placementTitle"
              name="placementTitle"
              value={formData.placementTitle}
              onChange={handleInputChange}
              className="job-upload-input"
                  placeholder="Enter Placement Article Title"
              required
            />
          </div>

          <div className="job-upload-field">
            <label htmlFor="placementSource" className="job-upload-label">
              Placement Article Source <span className="required">*</span>
            </label>
            <input
              type="text"
              id="placementSource"
              name="placementSource"
              value={formData.placementSource}
              onChange={handleInputChange}
              className="job-upload-input"
                  placeholder="Enter Placement Article Source"
              required
            />
          </div>

          <div className="job-upload-field">
            <label htmlFor="placementPicture" className="job-upload-label">
                  Placement Article Image <span className="required">*</span>
            </label>
                {existingPictureUrl && (
              <div className="file-preview" style={{ marginBottom: '1rem' }}>
                <img 
                      src={`http://127.0.0.1:3060/${existingPictureUrl}`}
                  alt="Current placement picture" 
                  className="file-preview-image"
                  style={{ maxWidth: '200px', maxHeight: '200px' }}
                />
                    <p>Current image: {existingPictureUrl}</p>
              </div>
            )}
            <input
              type="file"
              id="placementPicture"
              accept="image/*"
              onChange={(e) => handleFileChange(e, 'placementPicture')}
              className="job-upload-file-input"
            />
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#666' }}>
                  Leave empty to keep current image
            </small>
            {formData.placementPicture && (
              <div className="file-preview">
                <img 
                  src={URL.createObjectURL(formData.placementPicture)} 
                      alt="Picture preview" 
                  className="file-preview-image"
                />
                <span className="file-name">{formData.placementPicture.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, placementPicture: null }));
                      }}
                      style={{ marginLeft: '10px', color: '#ff0000', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Remove
                    </button>
              </div>
            )}
          </div>

          <div className="job-upload-field">
            <label htmlFor="placementShortDesc" className="job-upload-label">
                  Short Description <span className="required">*</span>
              <span className="char-count">({shortDescCharCount} / {MAX_SHORT_DESC} characters)</span>
            </label>
                {renderWysiwygToolbar('short')}
                <div
                  ref={shortDescEditorRef}
                  contentEditable
                  className="wysiwyg-editor"
                  style={{ minHeight: '150px', maxHeight: '200px', height: '150px' }}
                  onInput={handleShortDescInput}
                  onPaste={(e) => handlePaste(e, 'short')}
                  data-placeholder="Enter placement article short description here..."
            />
            {shortDescCharCount > MAX_SHORT_DESC && (
              <div className="char-count-error">
                Character limit exceeded. Please reduce the content.
              </div>
            )}
          </div>

          <div className="job-upload-field">
                <label htmlFor="createdDate" className="job-upload-label">
                  Created on <span className="required">*</span>
            </label>
                <input
                  type="date"
                  id="createdDate"
                  name="createdDate"
                  value={formData.createdDate}
                  onChange={handleInputChange}
                  className="job-upload-input"
                  required
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="job-upload-column job-upload-column-right">
              <div className="job-upload-field">
                <label htmlFor="placementLongDesc" className="job-upload-label">
                  Long Description <span className="required">*</span>
                  <span className="char-count">({longDescCharCount} / {MAX_LONG_DESC} characters)</span>
                </label>
                {renderWysiwygToolbar('long')}
            <div
                  ref={longDescEditorRef}
              contentEditable
              className="wysiwyg-editor"
                  onInput={handleLongDescInput}
                  onPaste={(e) => handlePaste(e, 'long')}
              data-placeholder="Enter placement article long description here..."
            />
            {longDescCharCount > MAX_LONG_DESC && (
              <div className="char-count-error">
                Character limit exceeded. Please reduce the content.
              </div>
            )}
          </div>
            </div>
          </div>

          <div className="job-upload-submit-container">
          <button type="submit" className="job-upload-submit-button">
              Submit
          </button>
          </div>
        </form>
      </div>

      {/* Confirmation Overlay */}
      {showConfirmation && (
        <div className="confirmation-overlay">
          <div className="confirmation-modal">
            <h2 className="confirmation-title">Confirm Submission</h2>
            <p className="confirmation-message">
              Are you sure that all information filled out is correct before continuing with the submission?
            </p>
            <div className="confirmation-buttons">
              <button
                type="button"
                onClick={handleSubmit}
                className="confirmation-btn confirmation-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Yes, Submit'}
              </button>
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                className="confirmation-btn confirmation-btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditPlacementNews;
